/**
 * The terminal's one notice strip.
 *
 * Decides nothing: `terminalNoticeOf` picks the state and this paints it, so
 * the ladder is tested without a socket and the component without a feed.
 * Replaces the old `tr-banner` and `NoFeedNotice`, which could both be on
 * screen at once saying contradictory things about the same silence.
 */

import { Link } from 'react-router-dom'
import { Notice, NoticeAction } from '@/components/Notice'
import { terminalNoticeOf, type TerminalNoticeInput } from './terminalNotice'

export interface TerminalNoticeProps extends TerminalNoticeInput {
  onReconnect: () => void
}

export function TerminalNotice({ onReconnect, ...input }: TerminalNoticeProps) {
  const notice = terminalNoticeOf(input)
  if (!notice) return null

  return (
    <Notice
      tone={notice.tone}
      title={notice.title}
      className="mx-3 my-2"
      testId="terminal-notice"
      action={
        notice.canReconnect ? (
          <NoticeAction onClick={onReconnect} testId="feed-reconnect">
            Reconnect
          </NoticeAction>
        ) : notice.linkAccounts ? (
          <Link
            to="/accounts"
            className="rounded px-1.5 py-0.5 text-[11px] font-semibold underline underline-offset-2 transition-opacity hover:opacity-80"
            data-testid="notice-accounts-link"
          >
            Open Accounts
          </Link>
        ) : undefined
      }
    >
      {/*
        FEED_STALE is a gate name, and the gate name is what gets quoted in a
        support question — so it keeps its monospace treatment inside an
        otherwise plain sentence.
      */}
      {notice.body.split('FEED_STALE').flatMap((part, i) =>
        i === 0
          ? [part]
          : [
              <span key={i} className="num font-semibold">
                FEED_STALE
              </span>,
              part,
            ],
      )}
    </Notice>
  )
}
